import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, LogOut, Search, UserRound } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { getRoutesForRole } from '@/config/routes.config';
import { usersService } from '@/services/users.service';
import { ProfileModal } from './ProfileModal';
import { correctionsService } from '@/services/corrections.service';
import { approvalFlowsService } from '@/services/approval-flows.service';

type NotificationItem = {
  id: string;
  title: string;
  message: string;
  createdAt: string;
  path: string;
};

export const TopBar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [query, setQuery] = React.useState('');
  const [showResults, setShowResults] = React.useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = React.useState(false);
  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [notifications, setNotifications] = React.useState<NotificationItem[]>([]);
  const [readIds, setReadIds] = React.useState<Record<string, boolean>>({});
  const [departamento, setDepartamento] = React.useState<string | null>(null);
  const [avatarTapCount, setAvatarTapCount] = React.useState(0);
  const [avatarGone, setAvatarGone] = React.useState(false);
  const [avatarHint, setAvatarHint] = React.useState<string | null>(null);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const profileMenuRef = React.useRef<HTMLDivElement>(null);
  const notificationsRef = React.useRef<HTMLDivElement>(null);
  const modalAvatarRef = React.useRef<HTMLButtonElement>(null);
  const avatarTimerRef = React.useRef<number | null>(null);

  const allowedRoutes = React.useMemo(() => {
    if (!user) return [];
    const routes = getRoutesForRole(user.roleSGC);
    const unique = routes.filter((r, i, arr) => arr.findIndex((x) => x.path === r.path) === i);
    return [{ path: '/', label: 'Inicio' }, ...unique.map((r) => ({ path: r.path, label: r.label }))];
  }, [user]);

  const currentLabel = React.useMemo(() => {
    const found = allowedRoutes.find((r) => r.path === location.pathname);
    return found?.label || 'Vista';
  }, [allowedRoutes, location.pathname]);

  const filteredRoutes = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allowedRoutes.slice(0, 5);
    return allowedRoutes
      .filter((r) => r.label.toLowerCase().includes(q) || r.path.toLowerCase().includes(q))
      .slice(0, 6);
  }, [allowedRoutes, query]);

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setShowResults(true);
      }
      if (e.key === 'Escape') {
        setShowResults(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  React.useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setProfileMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
    };
    if (profileMenuOpen || notificationsOpen) document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [profileMenuOpen, notificationsOpen]);

  React.useEffect(() => {
    let active = true;
    const loadDepto = async () => {
      if (!user?.nomina) {
        if (active) setDepartamento(null);
        return;
      }
      try {
        const depto = await usersService.getDepartamentoByNomina(user.nomina);
        if (active) setDepartamento(depto);
      } catch {
        if (active) setDepartamento(null);
      }
    };
    loadDepto();
    return () => {
      active = false;
    };
  }, [user?.nomina]);

  React.useEffect(() => {
    if (!user?.uid) return;
    try {
      const raw = localStorage.getItem(`sgc.notifications.read.v1.${user.uid}`);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, boolean>;
      if (parsed && typeof parsed === 'object') setReadIds(parsed);
    } catch {
      // ignore
    }
  }, [user?.uid]);

  React.useEffect(() => {
    if (!user?.uid) return;
    try {
      localStorage.setItem(`sgc.notifications.read.v1.${user.uid}`, JSON.stringify(readIds));
    } catch {
      // ignore
    }
  }, [readIds, user?.uid]);

  React.useEffect(() => {
    if (!user?.uid) {
      setNotifications([]);
      return;
    }

    let corrItems: NotificationItem[] = [];
    let flowItems: NotificationItem[] = [];

    const mergeAndSort = () => {
      const merged = [...corrItems, ...flowItems].sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setNotifications(merged.slice(0, 30));
    };

    const unsubs: Array<() => void> = [];

    if (user.roleSGC === 'Responsable_Calidad' || user.roleSGC === 'Administrador_Calidad') {
      unsubs.push(
        correctionsService.onCorrectionsForResponsable(user.uid, (items) => {
          corrItems = items
            .filter((c) => c.status === 'pendiente')
            .map((c) => ({
              id: `corr-${c.id}`,
              title: 'Nueva corrección',
              message: `${c.sentByName} solicitó ajustes en ${c.documentCode}`,
              createdAt: c.sentAt,
              path: '/quality/approvals',
            }));
          mergeAndSort();
        }, 12000)
      );

      unsubs.push(
        approvalFlowsService.onFlowsForResponsable(user.uid, (flows) => {
          flowItems = flows
            .filter((f) => f.status === 'pending_responsable' || f.status === 'rejected_to_responsable')
            .map((f) => ({
              id: `flow-resp-${f.id}`,
              title: f.status === 'rejected_to_responsable' ? 'Flujo rechazado' : 'Flujo pendiente',
              message: `${f.documentCode} - ${f.documentName}`,
              createdAt: f.updatedAt || f.createdAt,
              path: '/quality/approvals',
            }));
          mergeAndSort();
        }, 12000)
      );
    }

    if (user.roleSGC === 'Aprobador' || user.roleSGC === 'Revisor') {
      unsubs.push(
        approvalFlowsService.onPendingFlowsForApprover(user.uid, (flows) => {
          flowItems = flows.map((f) => ({
            id: `flow-approver-${f.id}`,
            title: 'Aprobación requerida',
            message: `${f.documentCode} - ${f.documentName}`,
            createdAt: f.updatedAt || f.createdAt,
            path: '/approvals',
          }));
          mergeAndSort();
        }, 12000)
      );
    }

    return () => {
      unsubs.forEach((unsub) => unsub());
    };
  }, [user?.uid, user?.roleSGC]);

  const clearAvatarTimer = () => {
    if (avatarTimerRef.current) {
      window.clearTimeout(avatarTimerRef.current);
      avatarTimerRef.current = null;
    }
  };

  const showAvatarHint = (text: string, ms: number = 1400) => {
    setAvatarHint(text);
    clearAvatarTimer();
    avatarTimerRef.current = window.setTimeout(() => setAvatarHint(null), ms);
  };

  const playAvatarSpin = (el: HTMLElement) => {
    el.animate(
      [
        { transform: 'perspective(500px) rotateY(0deg) scale(1)' },
        { transform: 'perspective(500px) rotateY(180deg) scale(1.08)' },
        { transform: 'perspective(500px) rotateY(360deg) scale(1)' },
      ],
      {
        duration: 900,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        iterations: 1,
      }
    );
  };

  const playAvatarPeek = (el: HTMLElement) => {
    const target = (el.firstElementChild as HTMLElement) ?? el;
    target.animate(
      [
        { transform: 'translateY(0) scale(1)', opacity: 1, offset: 0 },
        { transform: 'translateY(46px) scale(0.98)', opacity: 1, offset: 0.2 },
        { transform: 'translateY(46px) scale(0.98)', opacity: 1, offset: 0.88 },
        { transform: 'translateY(0) scale(1)', opacity: 1, offset: 1 },
      ],
      {
        duration: 3800,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        iterations: 1,
      }
    );
  };

  const playAvatarBack = (el: HTMLElement) => {
    el.animate(
      [
        { transform: 'translateX(-22px) rotate(-6deg)', opacity: 0.6 },
        { transform: 'translateX(10px) rotate(4deg)', opacity: 1 },
        { transform: 'translateX(0) rotate(0deg)', opacity: 1 },
      ],
      {
        duration: 700,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        iterations: 1,
      }
    );
  };

  const onProfileAvatarClick = (el: HTMLElement) => {
    if (avatarGone) return;

    const nextTap = avatarTapCount + 1;
    setAvatarTapCount(nextTap >= 3 ? 0 : nextTap);

    if (nextTap === 1) {
      playAvatarSpin(el);
      return;
    }

    if (nextTap === 2) {
      playAvatarPeek(el);
      return;
    }

    showAvatarHint('Bye...');
    el.animate(
      [
        { transform: 'translateX(0) scale(1)', opacity: 1 },
        { transform: 'translateX(82px) scale(0.9)', opacity: 0 },
      ],
      {
        duration: 450,
        easing: 'ease-in',
        iterations: 1,
      }
    );

    window.setTimeout(() => setAvatarGone(true), 430);
    clearAvatarTimer();
    avatarTimerRef.current = window.setTimeout(() => {
      setAvatarGone(false);
      setAvatarHint('Ya volví');
      window.setTimeout(() => {
        if (modalAvatarRef.current) playAvatarBack(modalAvatarRef.current);
      }, 40);
      avatarTimerRef.current = window.setTimeout(() => setAvatarHint(null), 1400);
    }, 5000);
  };

  React.useEffect(() => {
    if (!profileModalOpen) {
      setAvatarTapCount(0);
      setAvatarGone(false);
      setAvatarHint(null);
      clearAvatarTimer();
    }
  }, [profileModalOpen]);

  React.useEffect(() => () => clearAvatarTimer(), []);

  const handleGoTo = (path: string) => {
    navigate(path);
    setShowResults(false);
    setQuery('');
  };

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const markAsRead = (id: string) => {
    setReadIds((prev) => ({ ...prev, [id]: true }));
  };

  const markAllAsRead = () => {
    setReadIds((prev) => {
      const next = { ...prev };
      for (const item of notifications) next[item.id] = true;
      return next;
    });
  };

  const unreadCount = React.useMemo(
    () => notifications.filter((n) => !readIds[n.id]).length,
    [notifications, readIds]
  );

  const openNotification = (item: NotificationItem) => {
    markAsRead(item.id);
    setNotificationsOpen(false);
    navigate(item.path);
  };

  const openProfileModal = () => {
    setProfileMenuOpen(false);
    setProfileModalOpen(true);
  };

  const initials = user?.fullName
    ? user.fullName.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  return (
    <>
      <header className="sticky top-0 z-20 border-b border-white/10 bg-slate-900/60 backdrop-blur-xl">
        <div className="px-4 py-3 md:px-8 md:py-4 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-base md:text-lg font-semibold text-white truncate">{currentLabel}</h1>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <div className="relative">
              <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-xl border border-white/15 bg-slate-800/70 min-w-[260px]">
                <Search className="h-4 w-4 text-slate-400" />
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setShowResults(true);
                  }}
                  onFocus={() => setShowResults(true)}
                  onBlur={() => window.setTimeout(() => setShowResults(false), 120)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && filteredRoutes[0]) {
                      handleGoTo(filteredRoutes[0].path);
                    }
                  }}
                  placeholder="Buscar vistas..."
                  className="bg-transparent text-sm text-white placeholder:text-slate-400 w-full outline-none"
                />
              </div>
              {showResults && filteredRoutes.length > 0 && (
                <div className="hidden md:block absolute right-0 mt-2 w-full rounded-xl border border-white/15 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden">
                  {filteredRoutes.map((route) => (
                    <button
                      type="button"
                      key={route.path}
                      onClick={() => handleGoTo(route.path)}
                      className="w-full text-left px-3 py-2.5 hover:bg-white/10 transition-colors"
                    >
                      <p className="text-sm text-white">{route.label}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="relative" ref={notificationsRef}>
              <button
                type="button"
                onClick={() => {
                  setNotificationsOpen((prev) => !prev);
                  setProfileMenuOpen(false);
                }}
                className="relative p-2.5 rounded-xl border border-white/15 bg-slate-800/70 text-slate-200 hover:bg-slate-700/80 transition-colors"
                title="Notificaciones"
              >
                <Bell className="h-4 w-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-[10px] text-white flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <div className="absolute right-0 mt-2 w-[340px] max-h-[420px] rounded-xl border border-white/15 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden">
                  <div className="px-3 py-2.5 border-b border-white/10 flex items-center justify-between">
                    <p className="text-sm font-medium text-white">Notificaciones</p>
                    <button
                      type="button"
                      onClick={markAllAsRead}
                      className="inline-flex items-center gap-1 text-xs text-slate-300 hover:text-white transition-colors"
                    >
                      <CheckCheck className="h-3.5 w-3.5" />
                      Marcar todas
                    </button>
                  </div>
                  <div className="max-h-[360px] overflow-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center text-sm text-slate-400">
                        Sin notificaciones por ahora
                      </div>
                    ) : (
                      notifications.map((item) => {
                        const unread = !readIds[item.id];
                        return (
                          <button
                            type="button"
                            key={item.id}
                            onClick={() => openNotification(item)}
                            className={`w-full text-left px-3 py-2.5 border-b border-white/5 hover:bg-white/10 transition-colors ${
                              unread ? 'bg-indigo-500/10' : ''
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <span className={`mt-1.5 h-2 w-2 rounded-full ${unread ? 'bg-indigo-300' : 'bg-transparent'}`} />
                              <div className="min-w-0">
                                <p className="text-sm text-white truncate">{item.title}</p>
                                <p className="text-xs text-slate-300 truncate">{item.message}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">
                                  {new Date(item.createdAt).toLocaleString('es-MX')}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={profileMenuRef}>
              <button
                type="button"
                onClick={() => setProfileMenuOpen((prev) => !prev)}
                className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-xl border border-white/15 bg-slate-800/70 hover:bg-slate-700/80 transition-colors"
                title="Opciones de perfil"
              >
                {user?.photoUrl ? (
                  <img
                    src={user.photoUrl}
                    alt={user?.fullName || 'Usuario'}
                    className="h-8 w-8 rounded-full object-cover border border-white/20"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-500 to-violet-600 text-white text-xs font-bold flex items-center justify-center">
                    {initials}
                  </div>
                )}
                <div className="hidden lg:block">
                  <p className="text-sm text-white font-medium leading-tight max-w-[140px] truncate">{user?.fullName}</p>
                  <p className="text-[11px] text-slate-400 leading-tight truncate">{user?.roleSGC}</p>
                </div>
              </button>

              {profileMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-xl border border-white/15 bg-slate-900/95 backdrop-blur-xl shadow-2xl overflow-hidden">
                  <button
                    type="button"
                    onClick={openProfileModal}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-white hover:bg-white/10 transition-colors"
                  >
                    <UserRound className="h-4 w-4 text-slate-300" />
                    Ver perfil
                  </button>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm text-white hover:bg-white/10 transition-colors"
                  >
                    <LogOut className="h-4 w-4 text-slate-300" />
                    Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <ProfileModal
        open={profileModalOpen}
        user={user}
        initials={initials}
        departamento={departamento}
        avatarGone={avatarGone}
        avatarHint={avatarHint}
        modalAvatarRef={modalAvatarRef}
        onAvatarClick={onProfileAvatarClick}
        onClose={() => setProfileModalOpen(false)}
      />
    </>
  );
};
